import type { Formula, RiskPreferences } from '../types/fantasy-football'

// ─── Formula knowledge base ──────────────────────────────────
// Every number the app produces traces back to one of these formulas.
// Parameter values are tunable from the Formulas tab; the engine reads
// them through a flat key→value map so any recommendation can be
// audited against the exact parameter snapshot recorded with it.

export const FORMULAS: Formula[] = [
  {
    id: 'F1',
    name: 'Consensus projection',
    category: 'projection',
    formula: 'μᵢ = Σₛ wₛ·pᵢ,ₛ / Σₛ wₛ',
    inputs: ['pᵢ,ₛ — season point projection for player i from source s (Yahoo, ESPN, consensus aggregate)', 'wₛ — user trust weight per source (Risk tab)'],
    rationale:
      'No single projection source is reliably best; a trust-weighted mean shrinks idiosyncratic source error. Weights are user preferences, not fitted constants, so you can dial up whichever source has been most accurate for your league’s scoring.',
    usedIn: ['Player rankings', 'VORP (F5)', 'Win model (F10)'],
    parameters: [],
  },
  {
    id: 'F2',
    name: 'Projection uncertainty',
    category: 'risk',
    formula: 'σᵢ = √(k·σ²ₛₒᵤᵣ𝒸ₑ + G·σ²ᵥᵥₑₑₖ)   with G = games played (17 − bye)',
    inputs: ['σₛₒᵤᵣ𝒸ₑ — std-dev of the per-source season projections', 'σᵥᵥₑₑₖ — historical week-to-week scoring std-dev', 'k — disagreement amplifier'],
    rationale:
      'Two independent kinds of risk: sources disagreeing about the player’s true mean (epistemic), and week-to-week scoring noise (aleatoric). Both feed the variance penalty in F8; only the weekly component feeds the win model in F10.',
    usedIn: ['Risk-adjusted value (F8)', 'Win model (F10)'],
    parameters: [
      { key: 'f2.sourceDisagreementK', label: 'Source disagreement amplifier (k)', value: 1.5, defaultValue: 1.5, min: 0, max: 4, step: 0.1, description: 'How much weight source disagreement gets relative to weekly noise. 0 ignores disagreement entirely.' },
    ],
  },
  {
    id: 'F3',
    name: 'Injury availability adjustment',
    category: 'projection',
    formula: 'μ′ᵢ = μᵢ · aᵢ   where aᵢ ∈ {a_low, a_med, a_high} by injury-risk tier',
    inputs: ['μᵢ — consensus projection (F1)', 'injury-risk tier from status reports and age/usage profile'],
    rationale:
      'Season projections from most sources assume near-full availability. Discounting by an expected-games-played factor converts them to true expected value. Tiers are coarse on purpose — false precision here is worse than none.',
    usedIn: ['Adjusted points', 'VORP (F5)'],
    parameters: [
      { key: 'f3.availLow', label: 'Availability — low risk', value: 0.97, defaultValue: 0.97, min: 0.8, max: 1, step: 0.01, description: 'Expected fraction of projected value retained for low injury-risk players.' },
      { key: 'f3.availMedium', label: 'Availability — medium risk', value: 0.92, defaultValue: 0.92, min: 0.7, max: 1, step: 0.01, description: 'Expected fraction retained for medium injury-risk players.' },
      { key: 'f3.availHigh', label: 'Availability — high risk', value: 0.84, defaultValue: 0.84, min: 0.5, max: 1, step: 0.01, description: 'Expected fraction retained for high injury-risk players.' },
    ],
  },
  {
    id: 'F4',
    name: 'Schedule strength adjustment',
    category: 'schedule',
    formula: 'μ″ᵢ = μ′ᵢ · (1 + θ·(SOSₜₑₐₘ,ₚₒₛ − 1))',
    inputs: ['SOSₜₑₐₘ,ₚₒₛ — season schedule factor for the player’s NFL team at his position (1 = league average; >1 = softer slate of opposing defenses)', 'θ — schedule weight (Risk tab)'],
    rationale:
      'Opposing defenses are not interchangeable: the same player projects higher against a slate that leaks points to his position. θ lets you decide how much schedule signal to trust — schedule strength is itself a noisy preseason estimate.',
    usedIn: ['Adjusted points', 'VORP (F5)', 'Win model (F10)'],
    parameters: [],
  },
  {
    id: 'F5',
    name: 'Replacement level & VORP',
    category: 'valuation',
    formula: 'VORPᵢ = μ″ᵢ − μ″_repl(pos)   with repl(pos) = rank-(T·starters(pos) + streaming buffer) player at pos',
    inputs: ['μ″ᵢ — fully adjusted projection (F1→F4)', 'T — league team count', 'starters(pos) — starting slots per team at the position'],
    rationale:
      'Raw points overrate positions that score a lot everywhere (QB). What wins leagues is points above what you could get off waivers. Replacement is the best player still freely available once every team fills its starters, plus a buffer for streaming depth.',
    usedIn: ['Risk-adjusted value (F8)', 'Draft order (F12)', 'Acquisitions (F11)'],
    parameters: [
      { key: 'f5.streamBufferQB', label: 'Streaming buffer — QB', value: 2, defaultValue: 2, min: 0, max: 8, step: 1, description: 'Extra ranks below T×starters treated as freely available at QB.' },
      { key: 'f5.streamBufferRB', label: 'Streaming buffer — RB', value: 6, defaultValue: 6, min: 0, max: 12, step: 1, description: 'Extra ranks at RB (handcuffs and committee backs get rostered).' },
      { key: 'f5.streamBufferWR', label: 'Streaming buffer — WR', value: 6, defaultValue: 6, min: 0, max: 12, step: 1, description: 'Extra ranks at WR.' },
      { key: 'f5.streamBufferTE', label: 'Streaming buffer — TE', value: 2, defaultValue: 2, min: 0, max: 8, step: 1, description: 'Extra ranks at TE.' },
    ],
  },
  {
    id: 'F6',
    name: 'Teammate covariance',
    category: 'risk',
    formula: 'MCᵢ = γ · Σⱼ∈R ρ(posᵢ,posⱼ)·σᵥᵥ,ᵢ·σᵥᵥ,ⱼ · 1[teamᵢ = teamⱼ]',
    inputs: ['R — current roster', 'ρ(posᵢ,posⱼ) — same-NFL-team weekly correlation by position pair', 'σᵥᵥ — weekly std-devs', 'γ — teammate correlation weight (Risk tab)'],
    rationale:
      'Players on the same NFL team share game script, offensive health, and weather — idiosyncratic risk that does not diversify. A QB and his WR boom and bust together (ρ>0); two RBs in one backfield cannibalize touches (ρ<0, which is actually diversifying). MCᵢ is the marginal covariance a candidate adds to YOUR roster, so the same player is worth less to a roster already loaded with his teammates.',
    usedIn: ['Risk-adjusted value (F8)', 'Draft order (F12)', 'Win model (F10)'],
    parameters: [
      { key: 'f6.rhoQbWr', label: 'ρ same-team QB–WR', value: 0.35, defaultValue: 0.35, min: -1, max: 1, step: 0.05, description: 'Stacking correlation: QB passing points and WR receiving points move together.' },
      { key: 'f6.rhoQbTe', label: 'ρ same-team QB–TE', value: 0.3, defaultValue: 0.3, min: -1, max: 1, step: 0.05, description: 'Same mechanism as QB–WR, slightly weaker.' },
      { key: 'f6.rhoQbRb', label: 'ρ same-team QB–RB', value: 0.05, defaultValue: 0.05, min: -1, max: 1, step: 0.05, description: 'Weak: positive game-script link offset by pass/run tradeoff.' },
      { key: 'f6.rhoRbRb', label: 'ρ same-team RB–RB', value: -0.25, defaultValue: -0.25, min: -1, max: 1, step: 0.05, description: 'Committee backfields split a roughly fixed touch pool — negative correlation.' },
      { key: 'f6.rhoWrWr', label: 'ρ same-team WR–WR', value: 0.1, defaultValue: 0.1, min: -1, max: 1, step: 0.05, description: 'Mild: shared passing volume, offset by target competition.' },
      { key: 'f6.rhoWrTe', label: 'ρ same-team WR–TE', value: 0.05, defaultValue: 0.05, min: -1, max: 1, step: 0.05, description: 'Weak shared-passing-volume link.' },
      { key: 'f6.rhoOffK', label: 'ρ same-team offense–K', value: 0.15, defaultValue: 0.15, min: -1, max: 1, step: 0.05, description: 'Kickers score when the offense moves the ball.' },
      { key: 'f6.rhoOffDst', label: 'ρ same-team offense–DST', value: 0.05, defaultValue: 0.05, min: -1, max: 1, step: 0.05, description: 'Weak: good offenses give their defense leads and pass-rush chances.' },
    ],
  },
  {
    id: 'F7',
    name: 'Bye-week overlap penalty',
    category: 'risk',
    formula: 'Bᵢ = w_bye · c · m̄ᵢ · n_overlap(i, R)^e',
    inputs: ['m̄ᵢ — candidate’s weekly mean points', 'n_overlap — count of current starters sharing the candidate’s bye week', 'w_bye — bye penalty weight (Risk tab)'],
    rationale:
      'Byes are a perfectly correlated zero: every player with the same bye produces nothing in the same week. Losing 40 points in one week and 0 in another loses more games than losing 20 twice — weekly wins are what count (F10/F11) and a gutted week is a near-certain loss. The 1.5 exponent makes each additional overlapping starter hurt more than the last.',
    usedIn: ['Risk-adjusted value (F8)', 'Draft order (F12)'],
    parameters: [
      { key: 'f7.overlapExponent', label: 'Overlap exponent (e)', value: 1.5, defaultValue: 1.5, min: 1, max: 3, step: 0.1, description: 'Convexity of the penalty in the number of overlapping starters. 1 = linear.' },
      { key: 'f7.baseScale', label: 'Base scale (c)', value: 0.35, defaultValue: 0.35, min: 0, max: 1.5, step: 0.05, description: 'Fraction of one weekly mean charged per unit of overlap before the exponent.' },
    ],
  },
  {
    id: 'F8',
    name: 'Risk-adjusted value (certainty equivalent)',
    category: 'valuation',
    formula: 'RAVᵢ = VORPᵢ − λ·(σᵢ²/S + G·MCᵢ)/N − Bᵢ',
    inputs: ['VORPᵢ (F5)', 'σᵢ — season uncertainty (F2)', 'MCᵢ — marginal roster covariance (F6)', 'Bᵢ — bye penalty (F7)', 'λ — risk aversion (Risk tab)', 'S, N — scale normalizers'],
    rationale:
      'Mean–variance utility from portfolio theory applied to a roster: a player’s value is his expected production minus a price for the variance he adds. Crucially the variance term includes covariance with players you already hold — the same star is worth less to you if you already own his teammate or his bye week is already crowded. λ=0 recovers pure expected-points ranking.',
    usedIn: ['Player rankings', 'Draft order (F12)', 'Acquisitions (F11)'],
    parameters: [
      { key: 'f8.varianceScale', label: 'Variance normalizer (S)', value: 40, defaultValue: 40, min: 5, max: 200, step: 5, description: 'Divisor converting season variance to the points scale so λ≈1 is a sane default.' },
      { key: 'f8.covarianceScale', label: 'Covariance normalizer (N)', value: 10, defaultValue: 10, min: 1, max: 50, step: 1, description: 'Divisor for the seasonized covariance term.' },
    ],
  },
  {
    id: 'F9',
    name: 'Positional scarcity weighting',
    category: 'valuation',
    formula: 'SWVᵢ = RAVᵢ · s_pos   (s from the Risk tab, 1 = neutral)',
    inputs: ['RAVᵢ (F8)', 's_pos — user scarcity multiplier per position'],
    rationale:
      'You know your league. If WRs historically vanish from waivers after round 6 in your league, raise s_WR and every WR moves up the board and draft order proportionally. This is deliberately a transparent multiplier — easy to audit, impossible for the model to hide behind.',
    usedIn: ['Player rankings', 'Draft order (F12)'],
    parameters: [],
  },
  {
    id: 'F10',
    name: 'Weekly win probability',
    category: 'valuation',
    formula: 'P(win_w) = Φ((μ_R,w − μ_opp) / √(σ²_R,w + σ²_opp))',
    inputs: ['μ_R,w — sum of weekly means of starters active in week w (bye players replaced at replacement level)', 'σ²_R,w — starter variance incl. pairwise covariances (F6)', 'μ_opp, σ_opp — league-typical opposing lineup'],
    rationale:
      'Fantasy is head-to-head: the objective is games won, not points scored. Modeling the weekly margin as Normal gives a closed-form win probability. This is where correlation genuinely costs you — extra variance helps an underdog lineup but hurts a favorite, and the model prices that correctly each week.',
    usedIn: ['Season outlook', 'Acquisitions (F11)'],
    parameters: [
      { key: 'f10.oppMean', label: 'Opponent weekly mean', value: 112, defaultValue: 112, min: 60, max: 180, step: 1, description: 'Typical opposing starting-lineup weekly points for your league’s scoring.' },
      { key: 'f10.oppStdDev', label: 'Opponent weekly std-dev', value: 22, defaultValue: 22, min: 5, max: 60, step: 1, description: 'Week-to-week spread of opposing lineup totals.' },
    ],
  },
  {
    id: 'F11',
    name: 'Expected season wins & acquisition value',
    category: 'acquisition',
    formula: 'E[Wins] = Σᵥᵥ P(win_w);   ΔWinsᵢ = E[Wins | roster + i − drop] − E[Wins | roster]',
    inputs: ['P(win_w) from F10 for every scheduled week', 'candidate add/drop pair'],
    rationale:
      'The season objective in one number. An acquisition is evaluated by re-running the full weekly win model with the candidate slotted in (and the weakest same-position player dropped), so schedule fit, bye relief, and covariance effects all show up as won or lost games — the only currency that matters.',
    usedIn: ['Acquisition recommendations', 'Season outlook'],
    parameters: [],
  },
  {
    id: 'F12',
    name: 'Draft order construction',
    category: 'draft',
    formula: 'pick = argmaxᵢ SWVᵢ(R̂) · need(pos, R̂);  R̂ ← R̂ + pick, repeat',
    inputs: ['SWVᵢ — scarcity-weighted RAV recomputed against the simulated roster R̂ each round', 'need(pos, R̂) — multiplier by open starting/flex/bench slots'],
    rationale:
      'Greedy sequential simulation: each simulated pick updates the roster, so later picks see the covariance and bye penalties created by earlier ones — the board itself avoids stacking risk you did not opt into. The need multiplier stops the board recommending a fourth RB while a starting slot sits empty. Greedy is transparent and auditable; every slot records its score and reason.',
    usedIn: ['Draft tab — proposed board (requires your approval before it becomes the active order)'],
    parameters: [
      { key: 'f12.needOpenStarter', label: 'Need — open starting slot', value: 1, defaultValue: 1, min: 0.5, max: 2, step: 0.05, description: 'Multiplier when the position still has an open starting slot.' },
      { key: 'f12.needFlex', label: 'Need — flex-eligible only', value: 0.85, defaultValue: 0.85, min: 0.3, max: 1.5, step: 0.05, description: 'Multiplier when only a flex slot remains for the position.' },
      { key: 'f12.needBench', label: 'Need — bench only', value: 0.55, defaultValue: 0.55, min: 0.1, max: 1.2, step: 0.05, description: 'Multiplier when the position would ride the bench.' },
      { key: 'f12.lateRoundKDst', label: 'K/DST early-pick damper', value: 0.35, defaultValue: 0.35, min: 0.05, max: 1, step: 0.05, description: 'Damper applied to K and DST until starters elsewhere are filled — replacement level makes early K/DST picks wasteful.' },
    ],
  },
]

// ─── Parameter helpers ───────────────────────────────────────

export type ParamMap = Record<string, number>

export function defaultParams(): ParamMap {
  const map: ParamMap = {}
  for (const f of FORMULAS) for (const p of f.parameters) map[p.key] = p.defaultValue
  return map
}

/** Formulas with current (possibly overridden) parameter values merged in. */
export function formulasWithParams(params: ParamMap): Formula[] {
  return FORMULAS.map((f) => ({
    ...f,
    parameters: f.parameters.map((p) => ({ ...p, value: params[p.key] ?? p.defaultValue })),
  }))
}

export function defaultRiskPreferences(): RiskPreferences {
  return {
    riskAversion: 1.0,
    positionScarcity: { QB: 1, RB: 1, WR: 1, TE: 1, K: 1, DST: 1 },
    byePenaltyWeight: 1.0,
    teammateCorrelationWeight: 1.0,
    scheduleWeight: 0.5,
    sourceWeights: { yahoo: 1, espn: 1, consensus: 1.5 },
  }
}

/** Flatten prefs + params into one snapshot for audit records. */
export function parameterSnapshot(prefs: RiskPreferences, params: ParamMap): Record<string, number> {
  return {
    ...params,
    'prefs.riskAversion': prefs.riskAversion,
    'prefs.byePenaltyWeight': prefs.byePenaltyWeight,
    'prefs.teammateCorrelationWeight': prefs.teammateCorrelationWeight,
    'prefs.scheduleWeight': prefs.scheduleWeight,
    'prefs.scarcity.QB': prefs.positionScarcity.QB,
    'prefs.scarcity.RB': prefs.positionScarcity.RB,
    'prefs.scarcity.WR': prefs.positionScarcity.WR,
    'prefs.scarcity.TE': prefs.positionScarcity.TE,
    'prefs.scarcity.K': prefs.positionScarcity.K,
    'prefs.scarcity.DST': prefs.positionScarcity.DST,
    'prefs.source.yahoo': prefs.sourceWeights.yahoo ?? 1,
    'prefs.source.espn': prefs.sourceWeights.espn ?? 1,
    'prefs.source.consensus': prefs.sourceWeights.consensus ?? 1,
  }
}
